import type {
  EnterpriseBalanceAlertConfig,
  EnterpriseBalanceAlertPool,
  EnterpriseBalanceAlertPoolSummary,
  EnterpriseBalanceAlertResponse,
} from '~/services/AccountServer/types'
import {
  getEnterpriseQuotaSnapshot,
  type EnterpriseQuotaEntitlement,
} from '~/utils/enterpriseTrade'

export function createDefaultEnterpriseBalanceAlertConfig(): EnterpriseBalanceAlertConfig {
  return {
    enabled: true,
    thresholdType: 'PERCENT',
    thresholdPercent: 10,
    thresholdChars: null,
    monitorPools: ['VIP', 'AI'],
    emailEnabled: true,
    bannerEnabled: true,
  }
}

export function normalizeEnterpriseBalanceAlertPool(
  pool: unknown,
  fallback: EnterpriseBalanceAlertPool = 'LOCAL',
): EnterpriseBalanceAlertPool {
  const value = String(pool || '').toUpperCase()
  if (value === 'AI' || value === 'VIP') return value
  if (['CN', 'CHINA', 'DOMESTIC'].includes(value)) return 'VIP'
  if (['OVERSEA', 'OVERSEAS', 'INTERNATIONAL', 'GLOBAL'].includes(value)) return 'AI'
  if (['LOCAL', 'SINGLE', 'CURRENT'].includes(value)) return 'LOCAL'
  return fallback
}

export function normalizeEnterpriseBalanceAlertStatus(status: unknown): 'LOW' | 'EXHAUSTED' {
  const value = String(status || '').toUpperCase()
  if (['EXHAUSTED', 'EMPTY', 'ZERO', 'STOPPED'].includes(value)) return 'EXHAUSTED'
  return 'LOW'
}

export function normalizeEnterpriseBalanceAlertNumber(value: unknown): number {
  const normalized = typeof value === 'string' ? value.replace(/,/g, '') : value
  const numberValue = Number(normalized)
  return Number.isFinite(numberValue) ? numberValue : 0
}

export function normalizeEnterpriseBalanceAlertBoolean(value: unknown, fallback: boolean) {
  if (value === undefined || value === null || value === '') return fallback
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  const stringValue = String(value).toLowerCase()
  if (['true', '1', 'yes', 'on'].includes(stringValue)) return true
  if (['false', '0', 'no', 'off'].includes(stringValue)) return false
  return fallback
}

export function normalizeEnterpriseBalanceAlertConfig(
  config?: Partial<EnterpriseBalanceAlertConfig> | null,
): EnterpriseBalanceAlertConfig {
  const defaults = createDefaultEnterpriseBalanceAlertConfig()
  const thresholdType = config?.thresholdType === 'CHARS' ? 'CHARS' : 'PERCENT'
  const monitorPools = Array.isArray(config?.monitorPools)
    ? config.monitorPools.map(item => normalizeEnterpriseBalanceAlertPool(item)).filter(Boolean)
    : defaults.monitorPools

  return {
    enabled: normalizeEnterpriseBalanceAlertBoolean(config?.enabled, defaults.enabled),
    thresholdType,
    thresholdPercent: thresholdType === 'CHARS'
      ? null
      : normalizeEnterpriseBalanceAlertNumber(config?.thresholdPercent || defaults.thresholdPercent),
    thresholdChars: thresholdType !== 'CHARS' || config?.thresholdChars === null || config?.thresholdChars === undefined
      ? null
      : normalizeEnterpriseBalanceAlertNumber(config.thresholdChars),
    monitorPools: Array.from(new Set(monitorPools)),
    emailEnabled: normalizeEnterpriseBalanceAlertBoolean(config?.emailEnabled, defaults.emailEnabled),
    bannerEnabled: normalizeEnterpriseBalanceAlertBoolean(config?.bannerEnabled, defaults.bannerEnabled),
    version: config?.version ?? null,
  }
}

export function shouldShowEnterpriseBalanceAlert(
  alert: EnterpriseBalanceAlertPoolSummary,
  config: EnterpriseBalanceAlertConfig,
) {
  if (normalizeEnterpriseBalanceAlertStatus(alert.status) === 'EXHAUSTED') return true
  if (!config.enabled || !config.bannerEnabled) return false
  return !alert.dismissed
}

export function isEnterpriseBalanceAlertRouteExcluded(path: string) {
  const normalizedPath = String(path || '').toLowerCase()
  return normalizedPath.includes('/account/pay/')
    || /\/home\/(pdftranslate|readmarkdown)\/?$/.test(normalizedPath)
    || normalizedPath === '/'
    || /^\/(zh|en|ja|ko|tw|hi|ar|de|fr|id|itb|uk)\/?$/.test(normalizedPath)
}

export function sortEnterpriseBalanceAlerts(
  alerts: EnterpriseBalanceAlertPoolSummary[],
  currentPool: EnterpriseBalanceAlertPool,
) {
  return alerts.slice().sort((left, right) => {
    const leftStatus = normalizeEnterpriseBalanceAlertStatus(left.status)
    const rightStatus = normalizeEnterpriseBalanceAlertStatus(right.status)
    if (leftStatus !== rightStatus) return leftStatus === 'EXHAUSTED' ? -1 : 1

    const leftPool = normalizeEnterpriseBalanceAlertPool(left.pool)
    const rightPool = normalizeEnterpriseBalanceAlertPool(right.pool)
    if (leftPool === currentPool && rightPool !== currentPool) return -1
    if (rightPool === currentPool && leftPool !== currentPool) return 1
    return 0
  })
}

export function validateEnterpriseBalanceAlertCustomThreshold(value: unknown, poolTotals: unknown[]) {
  const threshold = normalizeEnterpriseBalanceAlertNumber(value)
  if (!Number.isInteger(threshold) || threshold <= 0) return '自定义字符数必须为正整数'

  const normalizedTotals = poolTotals
    .map(total => normalizeEnterpriseBalanceAlertNumber(total))
  if (normalizedTotals.length && threshold > Math.min(...normalizedTotals)) {
    return '自定义字符数不能超过所选池的最小总额度'
  }
  return ''
}

export function getEnterpriseBalanceAlertPurchaseMode(planMode: unknown): 'local' | 'global' {
  return planMode === 'global' ? 'global' : 'local'
}

export function canEditEnterpriseBalanceAlertNotificationEmail(
  isCreator: boolean,
  accountType: unknown,
) {
  const normalizedAccountType = Number(accountType)
  return isCreator
    && Number.isFinite(normalizedAccountType)
    && normalizedAccountType !== 0
}

/**
 * 独立套餐只存在一个当前空间。兼容后端仍返回 AI/VIP 池的旧响应，并用当前套餐
 * 的有效额度校正已取消全球套餐后残留的历史加油包总额。
 */
export function reconcileLocalEnterpriseBalanceAlert(
  response: EnterpriseBalanceAlertResponse,
  enterprise?: EnterpriseQuotaEntitlement | null,
): EnterpriseBalanceAlertResponse {
  if (response.planMode !== 'local') return response

  const config = normalizeEnterpriseBalanceAlertConfig(response.config)
  const quota = getEnterpriseQuotaSnapshot(enterprise)
  const thresholdChars = config.thresholdType === 'CHARS'
    ? Math.max(1, normalizeEnterpriseBalanceAlertNumber(config.thresholdChars))
    : Math.max(1, Math.floor(
        quota.totalWords * normalizeEnterpriseBalanceAlertNumber(config.thresholdPercent) / 100,
      ))
  const status = quota.remainingWords === 0 ? 'EXHAUSTED' as const : 'LOW' as const
  const isActiveAlert = quota.remainingWords === 0
    || (quota.remainingWords < thresholdChars && config.enabled)
  const sourceAlert = response.alerts?.[0]
  const alerts = sourceAlert && isActiveAlert
    ? [{
        ...sourceAlert,
        pool: 'LOCAL' as const,
        label: '当前空间',
        domain: null,
        totalWords: quota.totalWords,
        remainingWords: quota.remainingWords,
        thresholdChars,
        thresholdPercent: config.thresholdType === 'PERCENT' ? config.thresholdPercent : null,
        status,
        dismissible: status === 'LOW',
      }]
    : []

  return {
    ...response,
    currentPool: 'LOCAL',
    pools: [{
      pool: 'LOCAL',
      label: '当前空间',
      domain: null,
      totalWords: quota.totalWords,
      remainingWords: quota.remainingWords,
    }],
    alerts,
  }
}
